import { SelectionPillWidget } from '../../../widgets/SelectionPillWidget';
import { SiteRoute } from '@journeyapps/package-types';
import * as React from 'react';
import {
  faCloud,
  faGlobe,
  faCode,
  faMobileAndroid,
  faRocket,
  faServer,
  faUsers,
  faTerminal,
  faDatabase,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { SearchFieldWidget } from '../../../widgets/SearchFieldWidget';
import {
  FEATURE_SYSTEMS,
  PLATFORM_FEATURES,
  FeatureAvailability,
  FeatureNodeType,
  FEATURE_NODE_LABELS,
  FeatureSystemId,
  PlatformFeature
} from '../../../data/features';
import { FeatureSectionWidget } from './FeatureSectionWidget';
import { FeatureWidget } from './FeatureWidget';
import { S } from './FeatureExplorerStyles';
import {
  FEATURE_GOALS,
  FEATURE_LANGUAGES,
  FEATURE_TEAM_OPTIONS,
  FEATURE_GOVERNANCE_OPTIONS,
  FEATURE_TRAITS,
  FEATURE_BUILD_METHODS,
  getRecommendedFeatureIds
} from '../../../data/featureWizard';
import { FeatureSelection, featureSelectionParams } from './featureSelection';

const SYSTEM_ICONS = {
  oxide: faCode,
  cloudcode: faCloud,
  backend: faServer,
  runtime: faRocket,
  container: faMobileAndroid,
  'web-container': faGlobe,
  override: faUsers,
  sector: faDatabase,
  cli: faTerminal,
  source: faCode,
  build: faLayerGroup,
  deployments: faRocket,
  updates: faCloud,
  identity: faUsers,
  diagnostics: faTerminal,
  distribution: faMobileAndroid
} satisfies Record<FeatureSystemId, typeof faCode>;

interface PlatformFeaturesWidgetProps {
  selection: FeatureSelection;
  onSelectionChange: (selection: FeatureSelection) => void;
  headingRef?: React.Ref<HTMLHeadingElement>;
}

export const PlatformFeaturesWidget: React.FC<PlatformFeaturesWidgetProps> = ({
  selection,
  onSelectionChange,
  headingRef
}) => {
  const {
    goals: selectedGoals,
    traits: selectedTraits,
    methods: selectedMethods,
    language,
    team,
    governance,
    query,
    types
  } = selection;
  const setQuery = (value: string) => onSelectionChange({ ...selection, query: value });
  const wizardUrl = `${SiteRoute.FEATURE_FINDER}?${featureSelectionParams(selection)}`;
  const search = query.trim().toLowerCase();
  const recommendedIds = getRecommendedFeatureIds(
    selectedGoals,
    selectedTraits,
    selectedMethods,
    language,
    team,
    governance
  );
  const features = PLATFORM_FEATURES.filter((feature) => {
    const matchesGoal = selectedGoals.length === 0 || recommendedIds.has(feature.id);
    const matchesSearch =
      !search ||
      [
        feature.name,
        feature.description,
        ...feature.tags,
        ...feature.systems.map((id) => FEATURE_SYSTEMS.find((system) => system.id === id)!.name)
      ]
        .join(' ')
        .toLowerCase()
        .includes(search);
    return matchesGoal && matchesSearch;
  });
  const showFeatures = types.includes(FeatureNodeType.FEATURE);
  const toggleType = (type: FeatureNodeType) =>
    onSelectionChange({
      ...selection,
      types: types.includes(type) ? types.filter((item) => item !== type) : [...types, type]
    });

  const renderFeature = (feature: PlatformFeature) => (
    <FeatureWidget
      key={feature.id}
      label={feature.name}
      link={feature.links?.product}
      docsLink={feature.links?.docs}
      tag={
        feature.availability === FeatureAvailability.ENTERPRISE
          ? 'Enterprise'
          : feature.availability === FeatureAvailability.PRO
            ? 'Pro'
            : undefined
      }
      systems={
        feature.systems.length > 1 || !visibleSystemIds.has(feature.systems[0])
          ? feature.systems.map((id) => FEATURE_SYSTEMS.find((system) => system.id === id)!.name)
          : undefined
      }
    >
      {feature.description}
    </FeatureWidget>
  );
  const matchedSystemIds = new Set(features.flatMap((feature) => feature.systems));
  const visibleSystems = FEATURE_SYSTEMS.filter(
    (system) => types.includes(system.type) && matchedSystemIds.has(system.id)
  );
  const visibleSystemIds = new Set(visibleSystems.map((system) => system.id));
  const sharedFeatures = showFeatures
    ? features.filter((feature) => feature.systems.length !== 1 || !visibleSystemIds.has(feature.systems[0]))
    : [];

  return (
    <>
      <div>
        {selectedGoals.length > 0 ? (
          <S.Explorer>
            <S.ResultHeading>
              <S.StepHeading ref={headingRef} tabIndex={-1}>
                Your platform
              </S.StepHeading>
              <S.Edit to={wizardUrl} aria-label="Edit your selections">
                Edit
              </S.Edit>
            </S.ResultHeading>
            <S.Selections>
              {[
                { label: 'I want to', items: FEATURE_GOALS.filter((option) => selectedGoals.includes(option.id)) },
                { label: 'It needs to', items: FEATURE_TRAITS.filter((option) => selectedTraits.includes(option.id)) },
                {
                  label: 'Build using',
                  items: FEATURE_BUILD_METHODS.filter((option) => selectedMethods.includes(option.id))
                },
                { label: 'Language', items: FEATURE_LANGUAGES.filter((option) => option.id === language) },
                { label: 'Building', items: FEATURE_TEAM_OPTIONS.filter((option) => option.id === team) },
                ...(governance.length
                  ? [
                      {
                        label: 'Governance',
                        items: FEATURE_GOVERNANCE_OPTIONS.filter((option) => governance.includes(option.id))
                      }
                    ]
                  : [])
              ].map(({ label, items }) => (
                <S.SelectionRow key={label}>
                  <dt>{label}</dt>
                  <dd>
                    {items.length ? (
                      items.map((option) => <S.SelectionValue key={option.id}>{option.name}</S.SelectionValue>)
                    ) : (
                      <S.Unselected>Not specified</S.Unselected>
                    )}
                  </dd>
                </S.SelectionRow>
              ))}
            </S.Selections>
          </S.Explorer>
        ) : null}
        <S.Filters>
          <SearchFieldWidget
            aria-label="Search features"
            placeholder="Search features, tools, or capabilities…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setQuery('');
            }}
          />
          {query ? (
            <S.Reset
              type="button"
              onClick={() => {
                onSelectionChange({ ...selection, query: '' });
              }}
            >
              Clear search
            </S.Reset>
          ) : null}
        </S.Filters>
      </div>

      <S.Legend role="group" aria-label="Show platform element types">
        {Object.values(FeatureNodeType).map((type) => (
          <SelectionPillWidget key={type} tone={type} checked={types.includes(type)} onChange={() => toggleType(type)}>
            {FEATURE_NODE_LABELS[type]}
          </SelectionPillWidget>
        ))}
      </S.Legend>
      <S.Items>
        {visibleSystems.map((system) => {
          const items = features.filter((feature) => feature.systems.length === 1 && feature.systems[0] === system.id);

          return (
            <FeatureSectionWidget
              key={system.id}
              nodeType={system.type}
              id={`platform-${system.id}`}
              icon={SYSTEM_ICONS[system.id]}
              label={system.name}
              desc={system.description}
              link={'product' in system.links ? system.links.product : undefined}
              docsLink={system.links.docs}
              connections={(() => {
                const outgoing = system.connectsTo.filter((id) => visibleSystemIds.has(id as FeatureSystemId));
                const incoming = visibleSystems.filter(
                  (other) =>
                    (other.type === FeatureNodeType.INTERFACE || other.type === FeatureNodeType.TOOL) &&
                    (other.connectsTo as readonly string[]).includes(system.id)
                );
                if (!outgoing.length && !incoming.length) return undefined;
                return (
                  <S.Connections>
                    {outgoing.length > 0 ? (
                      <S.ConnectionRow>
                        <span>
                          {system.type === FeatureNodeType.INTERFACE || system.type === FeatureNodeType.TOOL
                            ? 'Access to'
                            : 'Works with'}
                        </span>
                        {outgoing.map((id) => {
                          const target = FEATURE_SYSTEMS.find((item) => item.id === id)!;
                          return (
                            <S.Connection key={id} href={`#platform-${id}`} nodeType={target.type}>
                              {target.name}
                            </S.Connection>
                          );
                        })}
                      </S.ConnectionRow>
                    ) : null}
                    {incoming.length > 0 ? (
                      <S.ConnectionRow>
                        <span>Access through</span>
                        {incoming.map((target) => (
                          <S.Connection key={target.id} href={`#platform-${target.id}`} nodeType={target.type}>
                            {target.name}
                          </S.Connection>
                        ))}
                      </S.ConnectionRow>
                    ) : null}
                  </S.Connections>
                );
              })()}
            >
              {showFeatures ? items.map(renderFeature) : null}
            </FeatureSectionWidget>
          );
        })}
        {sharedFeatures.length > 0 ? (
          <FeatureSectionWidget
            icon={faLayerGroup}
            label={types.length === 1 ? 'Features' : 'Shared & standalone features'}
            desc="Capabilities and their associated technologies."
          >
            {sharedFeatures.map(renderFeature)}
          </FeatureSectionWidget>
        ) : null}
        {visibleSystems.length === 0 && sharedFeatures.length === 0 ? (
          <S.Empty>Nothing matches these filters. Select a type or adjust your choices.</S.Empty>
        ) : null}
      </S.Items>
    </>
  );
};
